import React, { useLayoutEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Copy } from 'lucide-react';

/**
 * Blocurile din tabul „Privire generală" (modalul de container și cel de B/L):
 * grup cu antet, rând etichetă/valoare (cu copiere opțională) și rând de text lung extensibil.
 * Stilurile sunt în claude-design.css (`.ov-*`).
 */

export const OvGroup: React.FC<{ icon: React.ElementType; title: string; children: React.ReactNode }> = ({
  icon: Icon,
  title,
  children,
}) => (
  <section className="ov-group">
    <header className="ov-group-head">
      <Icon size={13} strokeWidth={2.2} />
      {title}
    </header>
    <div className="ov-group-body">{children}</div>
  </section>
);

export const OvRow: React.FC<{
  label: string;
  value: string | null | undefined;
  sub?: string | null;
  mono?: boolean;
  copy?: boolean;
}> = ({ label, value, sub, mono, copy }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      // clipboard indisponibil (http, permisiune refuzată) — nu blocăm nimic
    }
  };
  return (
    <div className="ov-row">
      <span className="ov-k">{label}</span>
      <span className={'ov-v' + (mono ? ' mono' : '')} title={value || undefined}>
        {value || '—'}
        {sub && <span className="ov-sub mono">{sub}</span>}
      </span>
      {copy && value ? (
        <button
          type="button"
          className={'ov-copy' + (copied ? ' copied' : '')}
          onClick={handleCopy}
          aria-label={copied ? 'Copiat' : `Copiază ${label}`}
          title={copied ? 'Copiat' : 'Copiază'}
        >
          {copied ? <Check size={13} strokeWidth={2.5} /> : <Copy size={13} strokeWidth={2} />}
        </button>
      ) : (
        copy && <span className="ov-copy-spacer" />
      )}
    </div>
  );
};

/**
 * Text lung (descrierea mărfii): pe calculator un singur rând, cu „Mai mult / Mai puțin” doar
 * când textul chiar nu încape; pe telefon (CSS) e afișat întreg, fără buton.
 */
export const OvLongRow: React.FC<{ label: string; value: string | null | undefined; isRo: boolean }> = ({
  label,
  value,
  isRo,
}) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return;
    const measure = () => {
      // măsurăm doar în starea restrânsă; extins, textul încape oricum
      if (el.classList.contains('expanded')) return;
      setOverflows(el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [value]);

  const canToggle = overflows || expanded;
  return (
    <div className="ov-row long">
      <div className="ov-long-head">
        <span className="ov-k">{label}</span>
        {canToggle && (
          <button
            type="button"
            className={'ov-expand' + (expanded ? ' open' : '')}
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
          >
            {expanded ? (isRo ? 'Mai puțin' : 'Less') : isRo ? 'Mai mult' : 'More'}
            <ChevronDown size={13} strokeWidth={2.2} />
          </button>
        )}
      </div>
      <span
        ref={textRef}
        className={'ov-v ov-long-text' + (expanded ? ' expanded' : '') + (canToggle && !expanded ? ' clickable' : '')}
        onClick={canToggle && !expanded ? () => setExpanded(true) : undefined}
      >
        {value || '—'}
      </span>
    </div>
  );
};

/** Numele din tabelul `port` conțin deja codul („ISTANBUL (TRIST)”); codul îl afișăm separat. */
export const stripPortCode = (name: string | null | undefined): string | null =>
  name ? name.replace(/\s*\([A-Z0-9]{5}\)\s*$/, '') || name : null;
